/**
 * Date helpers for the optional "needed by" deadline (upload form quick-picks + the
 * value sent to the server). Calendar-day arithmetic in the browser's local timezone —
 * the picker is a plain HTML date input, no time-of-day component.
 */

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** yyyy-mm-dd for an <input type="date"> value, in local time (not UTC). */
export function toDateInputValue(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function today(now: Date = new Date()): Date {
  return startOfDay(now);
}

export function tomorrow(now: Date = new Date()): Date {
  const d = startOfDay(now);
  d.setDate(d.getDate() + 1);
  return d;
}

/** The coming Friday - "by the end of this week" in business-day terms. */
export function endOfThisWeek(now: Date = new Date()): Date {
  const d = startOfDay(now);
  const day = d.getDay(); // 0 = Sunday .. 6 = Saturday
  const daysUntilFriday = (5 - day + 7) % 7 || 7;
  d.setDate(d.getDate() + daysUntilFriday);
  return d;
}

/** True if the given date-input value (yyyy-mm-dd) is strictly before today. */
export function isPastDate(value: string, now: Date = new Date()): boolean {
  if (!value) return false;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.getTime() < today(now).getTime();
}

/** yyyy-mm-dd -> an ISO timestamp (midnight local) suitable for the server, or null if empty/invalid. */
export function dateInputValueToIso(value: string): string | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}
